/**
 * DocNavigation Component (AC-652)
 *
 * A tree-view navigation component for the help documentation system.
 * Renders expandable categories with document items that users can navigate.
 *
 * Technical Design Specification:
 * - Renders a tree-view navigation with expandable categories
 * - Each category shows icon (emoji), title, and expand/collapse toggle
 * - Items within categories are clickable, trigger onSelect(path)
 * - Current document is highlighted (based on currentPath matching item.path)
 * - Categories start expanded if they contain currentPath, otherwise collapsed
 * - Show "No documentation available" when categories is empty
 * - Accessible ARIA tree structure (role="navigation", aria-expanded, aria-current)
 * - Keyboard navigation: Enter/Space to select items
 *
 * @see AC-652 - Create DocNavigation component
 * @see AC-648 - Epic: Contextual Help System
 */

import React, { useState, useEffect } from 'react';
import Tooltip from '../common/Tooltip';
import { DocCategory, DocNavigationProps } from '../../types/help';

/**
 * Icon mapping from icon names to display characters/emojis.
 */
const iconMap: Record<string, string> = {
  rocket: '🚀',
  sparkles: '✨',
  star: '⭐',
  wrench: '🔧',
  book: '📖',
  folder: '📁',
  question: '❓',
};

/**
 * Get icon display character for a given icon name.
 * Falls back to a default folder icon if not found.
 */
const getIconDisplay = (icon?: string): string => {
  if (!icon) return '';
  return iconMap[icon] || '📄';
};

/**
 * Extended props interface to support optional isLoading prop.
 */
interface DocNavigationPropsExtended extends DocNavigationProps {
  isLoading?: boolean;
}

/**
 * DocNavigation - Tree-view navigation component for help documentation.
 *
 * @param categories - Array of documentation categories to display
 * @param currentPath - Currently selected document path (for highlighting)
 * @param onSelect - Callback when a document item is selected
 * @param isLoading - Optional loading state
 */
const DocNavigation: React.FC<DocNavigationPropsExtended> = ({
  categories,
  currentPath,
  onSelect,
  isLoading = false,
}) => {
  // Track which categories are expanded - start with empty set
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  /**
   * Auto-expand the category containing currentPath on initial render
   * and whenever currentPath changes.
   */
  useEffect(() => {
    if (currentPath && categories.length > 0) {
      const categoryToExpand = categories.find((category) =>
        category.items.some((item) => item.path === currentPath)
      );
      if (categoryToExpand) {
        setExpandedCategories((prev) => {
          const newSet = new Set(prev);
          newSet.add(categoryToExpand.id);
          return newSet;
        });
      }
    }
  }, [currentPath, categories]);

  /**
   * Check if a category contains the current document path.
   */
  const categoryContainsCurrentPath = (categoryId: string): boolean => {
    if (!currentPath) return false;
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return false;
    return category.items.some((item) => item.path === currentPath);
  };

  /**
   * Toggle category expansion state.
   * If the category contains the currentPath, it cannot be collapsed (stays expanded).
   */
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        // Only collapse if the category doesn't contain currentPath
        if (!categoryContainsCurrentPath(categoryId)) {
          newSet.delete(categoryId);
        }
        // If it contains currentPath, don't collapse (no-op)
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  /**
   * Handle category header click.
   */
  const handleCategoryClick = (categoryId: string) => {
    toggleCategory(categoryId);
  };

  /**
   * Handle category header keyboard events.
   * Toggle on Enter or Space.
   */
  const handleCategoryKeyDown = (
    event: React.KeyboardEvent,
    categoryId: string
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleCategory(categoryId);
    }
  };

  /**
   * Handle document item click.
   */
  const handleItemClick = (path: string) => {
    onSelect(path);
  };

  /**
   * Handle document item keyboard events.
   * Select on Enter.
   */
  const handleItemKeyDown = (event: React.KeyboardEvent, path: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(path);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="doc-navigation-skeleton"
        role="status"
        aria-label="Loading navigation"
        className="animate-pulse space-y-3 p-4"
      >
        {/* Skeleton items */}
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-full ml-4"></div>
        <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-5/6 ml-4"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-4/5 ml-4"></div>
      </div>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <nav role="navigation" className="p-4">
        <p className="text-gray-500 dark:text-gray-400 text-sm">No documentation available</p>
      </nav>
    );
  }

  return (
    <nav role="navigation" className="p-2">
      <ul className="space-y-1">
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const hasIcon = !!category.icon;

          return (
            <li key={category.id}>
              {/* Category Header */}
              <button
                type="button"
                onClick={() => handleCategoryClick(category.id)}
                onKeyDown={(e) => handleCategoryKeyDown(e, category.id)}
                aria-expanded={isExpanded}
                aria-label={`${category.title}, ${category.items.length} items, ${isExpanded ? 'expanded' : 'collapsed'}`}
                className="w-full flex items-center px-2 py-2 text-left text-sm font-semibold text-gray-900 dark:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                {/* Expand/Collapse Icon */}
                <svg
                  className={`mr-2 h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>

                {/* Category Icon */}
                {hasIcon && (
                  <span
                    data-testid={`category-icon-${category.icon}`}
                    aria-hidden="true"
                    className="mr-2"
                  >
                    {getIconDisplay(category.icon)}
                  </span>
                )}

                {/* Category Title */}
                <span className="truncate">{category.title}</span>
              </button>

              {/* Category Items */}
              {isExpanded && (
                <ul className="mt-1 ml-4 space-y-1 border-l-2 border-gray-100 dark:border-gray-700 pl-2">
                  {category.items.length === 0 ? (
                    <li className="px-2 py-1 text-sm text-gray-500 dark:text-gray-400 italic">
                      No items in this category
                    </li>
                  ) : (
                    category.items.map((item) => {
                      const isSelected = currentPath === item.path;
                      // Build full title with description for tooltip
                      const fullTitle = item.description
                        ? `${item.title} - ${item.description}`
                        : item.title;

                      return (
                        <li key={item.id}>
                          <Tooltip content={fullTitle}>
                          <button
                            type="button"
                            data-testid="doc-item"
                            onClick={() => handleItemClick(item.path)}
                            onKeyDown={(e) => handleItemKeyDown(e, item.path)}
                            aria-current={isSelected ? 'page' : undefined}
                            className={`w-full text-left px-2 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${
                              isSelected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-warm-white'
                            }`}
                          >
                            <span className="block truncate">{item.title}</span>
                          </button>
                          </Tooltip>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default DocNavigation;
